import React, { useEffect, useState } from 'react'
import { Spin, Switch } from 'antd'
import { CaretDown, CaretUp } from '@phosphor-icons/react'
import { leagueEventStore } from 'state/league/leagueEvent'
import {
  useLeagueSettingStep,
  useLeagueStep11Communication,
} from 'api/league/LeagueCreation/leagueCreation'
import { useGetFaqs } from 'api/Faq/Faq'
import { useGetItemsToBring } from 'api/Listings/itemsToBring'
import { useGetAgreements } from 'api/Agreements/Agreeements'
import { useGetOrgDocuments } from 'api/EventCreation/creationStep5'
import { LeagueFinalResp } from 'types/league/league'
import {
  RequiredDeatilsTypes,
  KidsRequiredDetails,
  ParentRequiredDetailsType,
} from 'types/event/eventStep5'
import { successMessage, failureMessage } from 'api/global'
import FaqModal from 'pages/Events/Views/Common/Modals/FaqModal'
import ItemsToBringModal from 'pages/Events/Views/Common/Modals/ItemsToBring'
import AgreementsModal from 'pages/Events/Views/Common/Modals/AgreementsModal'
import RequiredDocument from 'pages/Events/Views/Common/Modals/RequiredDocuments/RequiredDocument'
import RequiredDetailsModal from 'pages/Events/Views/Common/Modals/RequiredDetails'
import SecurityModal from 'pages/Events/Views/Common/Modals/SecurityModal'
import AddTemplateToLeague from 'pages/Events/Views/CreateLeague/screens/Step7/Modals/ScheduleEmail/AddTemplateToLeague'
import RulesAndregulationModal from 'pages/Events/Views/CreateLeague/screens/Step7/Modals/RulesAndRegulation/RulesAndregulation'
import '../../styles/settingstab.scss'

/* ── Types ─────────────────────────────────────────────── */
type SectionKey =
  | 'faq'
  | 'items'
  | 'rules'
  | 'agreements'
  | 'mandatory'
  | 'documents'
  | 'email'
  | 'mentor'
  | 'security'

interface FaqDisplay {
  id: string
  question: string
  answer: string
  expanded: boolean
}
type SecurityLevel = 'low' | 'medium' | 'high'

/* ── Same helper as Step 7 ──────────────────────────────── */
const mapToKeyValueObject = (arr: RequiredDeatilsTypes[]) =>
  arr?.reduce(
    (acc, item) => {
      acc[item.key] = item.value
      return acc
    },
    {} as ParentRequiredDetailsType | KidsRequiredDetails,
  )

/* ── Sidebar nav — mirrors Step 7 section order ─────────── */
const NAV: { key: SectionKey; label: string }[] = [
  { key: 'faq', label: 'FAQ' },
  { key: 'items', label: 'Items to bring' },
  { key: 'agreements', label: 'Agreements' },
  { key: 'documents', label: 'Required documents' },
  { key: 'email', label: 'Scheduled emails' },
  { key: 'security', label: 'Security' },
]

/* ── Main component ──────────────────────────────────────── */
const SettingsTab: React.FC = () => {
  /* ── Store state — same fields as Step 7 ── */
  const {
    leagueData,
    setLeagueData,
    agreements,
    setAgreements,
    itemsToBring,
    setItemsToBring,
    selectedQuestions,
    setSelectedQuestions,
    selectedRequiredDocumentIds,
    setSelectedRequiredDocumentIds,
    rulesAndRegulation,
    setRulesAndRegulation,
    riskLevel,
    setRiskLevel,
    requiredDetails,
    setRequiredDetails,
    rulesAndRegulationModal,
    setRulesAndRegulationModal,
  } = leagueEventStore(store => ({
    leagueData: store.leagueData as LeagueFinalResp,
    setLeagueData: store.setLeagueData,
    agreements: store.agreements,
    setAgreements: store.setAgreements,
    itemsToBring: store.itemsToBring,
    setItemsToBring: store.setItemsToBring,
    selectedQuestions: store.selectedQuestions,
    setSelectedQuestions: store.setSelectedQuestions,
    selectedRequiredDocumentIds: store.selectedRequiredDocumentIds,
    setSelectedRequiredDocumentIds: store.setSelectedRequiredDocumentIds,
    rulesAndRegulation: store.rulesAndRegulation,
    setRulesAndRegulation: store.setRulesAndRegulation,
    riskLevel: store.riskLevel,
    setRiskLevel: store.setRiskLevel,
    requiredDetails: store.requiredDetails,
    setRequiredDetails: store.setRequiredDetails,
    rulesAndRegulationModal: store.rulesAndRegulationModal,
    setRulesAndRegulationModal: store.setRulesAndRegulationModal,
  }))

  const id = leagueData?._id || ''

  /* ── UI state ── */
  const [activeSection, setActiveSection] = useState<SectionKey>('faq')
  const [faqModalOpen, setFaqModalOpen] = useState(false)
  const [itemsModalOpen, setItemsModalOpen] = useState(false)
  const [agrModalOpen, setAgrModalOpen] = useState(false)
  const [docModalOpen, setDocModalOpen] = useState(false)
  const [mandatoryModalOpen, setMandatoryModalOpen] = useState(false)
  const [securityModalOpen, setSecurityModalOpen] = useState(false)
  const [emailModalOpen, setEmailModalOpen] = useState(false)

  /* ── FAQ display ── */
  const [faqList, setFaqList] = useState<FaqDisplay[]>([])
  const { data: faqsData, isLoading: faqsLoading, refetch: refetchFaqs } =
    useGetFaqs(id, 'league')

  /* ── Full data fetches — same hooks the modals use (shared RQ cache) ── */
  const { data: allItemsData, refetch: refetchItems } = useGetItemsToBring({
    organizationId: leagueData?.organizationId,
  })
  const { data: allAgreementsData, refetch: refetchAgreements } =
    useGetAgreements({ leagueId: id })
  const { data: allDocsData, refetch: refetchDocs } = useGetOrgDocuments(
    leagueData?.organizationId,
  )

  /* ── Security — separate endpoint, kept local ── */
  const [security, setSecurity] = useState<'low' | 'medium' | 'high'>('medium')
  const [allowDonation, setAllowDonation] = useState(false)

  /* ── Init store state from leagueData — same as Step 7's useEffect ── */
  useEffect(() => {
    if (!id) return
    setItemsToBring(leagueData?.itemsToBringIds || [])
    setSelectedQuestions(leagueData?.faqIds || [])
    setSelectedRequiredDocumentIds(leagueData?.requiredDocumentIds || [])
    setAgreements(leagueData?.agreementIds || [])
    setRulesAndRegulation((leagueData as any)?.regulationIds || [])
    refetchFaqs()
    refetchItems()
    refetchAgreements()
    refetchDocs()
  }, [id])

  useEffect(() => {
    if (faqsData) {
      setFaqList(
        faqsData.map(f => ({
          id: f._id,
          question: f.question,
          answer: f.answer || '',
          expanded: false,
        })),
      )
    }
  }, [faqsData])

  useEffect(() => {
    if (leagueData?.security) setSecurity(leagueData.security)
    if (typeof leagueData?.allowDonation === 'boolean')
      setAllowDonation(leagueData.allowDonation)
  }, [leagueData?._id])

  /* ── API mutations ── */
  const { mutate: saveSettings, isLoading: isSaving } = useLeagueSettingStep()
  const { mutate: saveCommunication, isLoading: isSavingComm } =
    useLeagueStep11Communication()

  /* ── Save all communication — same payload as Step 7's hanldeSubmit ── */
  const handleSaveCommunication = () => {
    const payload: any = {
      itemsToBringIds: itemsToBring,
      faqIds: selectedQuestions,
      agreementIds: agreements,
      requiredDocumentIds: selectedRequiredDocumentIds,
      regulationIds: rulesAndRegulation,
    }
    if (requiredDetails?.guardians) {
      payload.parentRequiredDetails = mapToKeyValueObject(
        requiredDetails.guardians,
      )
      payload.authorizedAdultRequiredDetails = mapToKeyValueObject(
        requiredDetails.adults,
      )
      payload.kidRequiredDetails = mapToKeyValueObject(requiredDetails.kids)
    }
    saveCommunication(
      { leagueId: id, payload },
      {
        onSuccess: data => {
          setLeagueData({ ...leagueData, ...data })
        },
        onError: () => failureMessage('Failed to save'),
      },
    )
  }

  const handleSaveSecurity = () => {
    saveSettings(
      {
        leagueId: id,
        payload: { security, allowDonation, preSelectedDonationAmounts: [] },
      },
      {
        onSuccess: () => successMessage('Security settings saved'),
        onError: () => failureMessage('Failed to save'),
      },
    )
  }

  const toggleFaqExpand = (faqId: string) =>
    setFaqList(prev =>
      prev.map(f => (f.id === faqId ? { ...f, expanded: !f.expanded } : f)),
    )

  const SaveBtn = () => (
    <div className="st-save-row">
      <button
        className="button btn-primary"
        onClick={handleSaveCommunication}
        disabled={isSavingComm}
      >
        {isSavingComm ? 'Saving...' : 'Save changes'}
      </button>
    </div>
  )

  /* ── Content renderer ── */
  const renderContent = () => {
    switch (activeSection) {
      /* ─── FAQ ─── */
      case 'faq':
        return (
          <>
            <div className="st-content-header">
              <div>
                <h2 className="st-content-title">FAQ</h2>
                <p className="st-content-desc">
                  Select questions that could be of interest to participants.
                </p>
              </div>
              <button
                className="st-text-btn"
                onClick={() => setFaqModalOpen(true)}
              >
                Manage FAQs
              </button>
            </div>
            {faqsLoading ? (
              <Spin style={{ margin: 20 }} />
            ) : (
              <div className="st-list">
                {faqList
                  .filter(f => selectedQuestions.includes(f.id))
                  .map(faq => (
                    <div key={faq.id} className="st-faq-item">
                      <div
                        className="st-faq-row"
                        onClick={() => toggleFaqExpand(faq.id)}
                      >
                        <div className="st-faq-left">
                          <span className="st-checkbox st-checkbox-checked" />
                          <span className="st-faq-question">{faq.question}</span>
                        </div>
                        <button className="st-faq-caret">
                          {faq.expanded ? (
                            <CaretUp size={18} />
                          ) : (
                            <CaretDown size={18} />
                          )}
                        </button>
                      </div>
                      {faq.expanded && faq.answer && (
                        <div className="st-faq-answer">{faq.answer}</div>
                      )}
                    </div>
                  ))}
                {selectedQuestions.length === 0 && (
                  <p className="st-empty-text">No FAQs selected yet</p>
                )}
              </div>
            )}
            <SaveBtn />
            {faqModalOpen && (
              <FaqModal
                openModal={faqModalOpen}
                setOpenModal={setFaqModalOpen}
                organizationId={leagueData?.organizationId}
                referenceId={id}
                reference="league"
                selectedFaqs={selectedQuestions}
                setSelectedFaqs={setSelectedQuestions}
              />
            )}
          </>
        )

      /* ─── Items to bring ─── */
      case 'items':
        return (
          <>
            <div className="st-content-header">
              <div>
                <h2 className="st-content-title">Items to bring</h2>
                <p className="st-content-desc">
                  Specify items participants are required or recommended to bring.
                </p>
              </div>
              <button
                className="st-text-btn"
                onClick={() => setItemsModalOpen(true)}
              >
                Manage items
              </button>
            </div>
            <div className="st-list">
              {(allItemsData || [])
                .filter(i => itemsToBring.includes(i._id))
                .map(item => (
                  <div key={item._id} className="st-faq-item">
                    <div className="st-faq-row">
                      <div className="st-faq-left">
                        <span className="st-checkbox st-checkbox-checked" />
                        <span className="st-faq-question">{item.name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              {itemsToBring.length === 0 && (
                <p className="st-empty-text">No items added yet</p>
              )}
            </div>
            <SaveBtn />
            {itemsModalOpen && (
              <ItemsToBringModal
                openModal={itemsModalOpen}
                closeModal={setItemsModalOpen}
                prevItems={itemsToBring}
                setPrevItems={setItemsToBring}
                organizationId={leagueData?.organizationId}
              />
            )}
          </>
        )
  

      /* ─── Agreements ─── */
      case 'agreements':
        return (
          <>
            <div className="st-content-header">
              <div>
                <h2 className="st-content-title">Agreements</h2>
                <p className="st-content-desc">
                  Manage agreements participants must accept before registering.
                </p>
              </div>
              <button
                className="st-text-btn"
                onClick={() => setAgrModalOpen(true)}
              >
                Manage agreements
              </button>
            </div>
            <div className="st-list">
              {(allAgreementsData || [])
                .filter(a => agreements.includes(a._id))
                .map(agr => (
                  <div key={agr._id} className="st-faq-item">
                    <div className="st-faq-row">
                      <div className="st-faq-left">
                        <span className="st-checkbox st-checkbox-checked" />
                        <span className="st-faq-question">{agr.agreement}</span>
                      </div>
                    </div>
                  </div>
                ))}
              {agreements.length === 0 && (
                <p className="st-empty-text">No agreements added yet</p>
              )}
            </div>
            <SaveBtn />
            {agrModalOpen && (
              <AgreementsModal
                modalOpen={agrModalOpen}
                setModalOpen={setAgrModalOpen}
                type="league"
                entityId={id}
                selectedAgreementsIds={agreements}
                setSelectedAgreementsIds={setAgreements}
              />
            )}
          </>
        )

      /* ─── Required Documents ─── */
      case 'documents':
        return (
          <>
            <div className="st-content-header">
              <div>
                <h2 className="st-content-title">Required Documents</h2>
                <p className="st-content-desc">
                  Select documents participants must upload before registering.
                </p>
              </div>
              <button
                className="st-text-btn"
                onClick={() => setDocModalOpen(true)}
              >
                Manage documents
              </button>
            </div>
            <div className="st-list">
              {(allDocsData || [])
                .filter(d => selectedRequiredDocumentIds.includes(d.id))
                .map(doc => (
                  <div key={doc.id} className="st-faq-item">
                    <div className="st-faq-row">
                      <div className="st-faq-left">
                        <span className="st-checkbox st-checkbox-checked" />
                        <span className="st-faq-question">{doc.name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              {selectedRequiredDocumentIds.length === 0 && (
                <p className="st-empty-text">No required documents</p>
              )}
            </div>
            <SaveBtn />
            {docModalOpen && (
              <RequiredDocument
                openModal={docModalOpen}
                closeModal={setDocModalOpen}
                organizationId={leagueData?.organizationId}
                referenceId={id}
                reference="league"
                selectedRequiredDocumentIds={selectedRequiredDocumentIds}
                setSelectedRequiredDocumentIds={setSelectedRequiredDocumentIds}
              />
            )}
          </>
        )

      /* ─── Scheduled Emails ─── */
      case 'email':
        return (
          <>
            <div className="st-content-header">
              <div>
                <h2 className="st-content-title">Scheduled Emails</h2>
                <p className="st-content-desc">
                  Configure automated email templates to send to participants.
                </p>
              </div>
              <button
                className="st-text-btn"
                onClick={() => setEmailModalOpen(true)}
              >
                Manage emails
              </button>
            </div>
            <div className="st-list">
              {leagueData?.emailTemplates?.length ? (
                leagueData.emailTemplates.map(t => (
                  <div key={t._id} className="st-faq-item">
                    <div className="st-faq-row">
                      <div className="st-faq-left">
                        <span className="st-checkbox st-checkbox-checked" />
                        <span className="st-faq-question">
                          {(t as any).name || (t as any).subject || t._id}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="st-empty-text">No email templates added yet</p>
              )}
            </div>
            {emailModalOpen && (
              <AddTemplateToLeague
                openModal={emailModalOpen}
                setOpenModal={setEmailModalOpen}
              />
            )}
          </>
        )
      /* ─── Security ─── */
      case 'security':
        return (
          <>
            <div className="st-content-header">
            </div>

            <div className="st-subsection-header" style={{ marginBottom: 12 }}>
              <span className="st-subsection-title">Security level</span>
            </div>
            <div className="st-security-group">
              {(['low', 'medium', 'high'] as SecurityLevel[]).map(level => (
                <button
                  key={level}
                  className={`st-security-btn ${security === level ? 'st-security-active' : ''}`}
                  onClick={() => setSecurity(level)}
                >
                  <span className={`st-security-dot st-security-dot-${level}`} />
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </button>
              ))}
            </div>

            <div
              className="st-subsection-header"
              style={{ marginTop: 32, marginBottom: 12 }}
            >
              <span className="st-subsection-title">League status</span>
            </div>
            <div className="st-status-row">
              <span
                className={`analytics-status-badge analytics-status-${leagueData?.status}`}
              >
                {leagueData?.status
                  ? leagueData.status.charAt(0).toUpperCase() +
                    leagueData.status.slice(1)
                  : '—'}
              </span>
              <button
                className="st-text-btn"
                onClick={() => setSecurityModalOpen(true)}
              >
                Configure
              </button>
            </div>

            <div
              className="st-subsection-header"
              style={{ marginTop: 32, marginBottom: 12 }}
            >
              <span className="st-subsection-title">Allow donations</span>
            </div>
            <div className="st-toggle-list">
              <div className="st-toggle-row">
                <div className="st-toggle-info">
                  <span className="st-toggle-label">
                    Enable donation collection
                  </span>
                  <span className="st-toggle-desc">
                    Allow participants to make optional donations during
                    registration.
              </span>
                </div>
                <Switch
                  checked={allowDonation}
                  onChange={setAllowDonation}
                  className={allowDonation ? 'st-switch-on' : ''}
                />
              </div>
            </div>

            <div className="st-save-row">
              <button
                className="button btn-primary"
                onClick={handleSaveSecurity}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
            {securityModalOpen && (
              <SecurityModal
                openModal={securityModalOpen}
                closeModal={setSecurityModalOpen}
                riskLevel={security}
                setRiskLevel={setSecurity}
              />
            )}
          </>
        )

      default:
        return null
    }
  }

  return (
    <div className="st-container">
      {/* ── Left sidebar ── */}
      <div className="st-sidebar">
        {NAV.map(item => (
          <button
            key={item.key}
            className={`st-nav-item ${activeSection === item.key ? 'st-nav-active' : ''}`}
            onClick={() => setActiveSection(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* ── Right content ── */}
      <div className="st-content">{renderContent()}</div>
    </div>
  )
}

export default SettingsTab
